import React, { useState, useRef, useEffect } from "react";
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css'

const Controls = ({
  isRunning,
  handleStartPause,
  handleReset,
  handleLongReset,
  handleToggle,
  selectedTimer,
  incrementFocusTime,
  decrementFocusTime,
  incrementRestTime,
  decrementRestTime,
  handlePomodoroMode,
  handlePreset,
  handleSavePreset3,
  // Settings props:
  currentSetting,
  brightness,
  setBrightness,
  volume,
  setVolume,
  tone,
  setTone,
  handleNextSetting,
  // The function that enters settings after 2s hold
  enterSettingsMode,
  exitSettingsMode,
  // On Off
  setIsDeviceOn,
  isDeviceOn,
}) => {
  const [holdInterval, setHoldInterval] = useState(null);
  const [holdCheckInterval, setHoldCheckInterval] = useState(null);
  const hasSavedPreset3 = useRef(false); // For preset saving
  const [holdSettingsTimeout, setHoldSettingsTimeout] = useState(null);

  const wasLongPress = useRef(false);

  // === ADDED FOR RESET ===
  const resetPressTimer = useRef(null);
  const hasPerformedLongPress = useRef(false);

  // === ADDED FOR DEVICE ON/OFF ===
  const wasStartPauseLongPress = useRef(false);
  const [startPauseTimeout, setStartPauseTimeout] = useState(null);


  // Toggle device power
  const toggleDevicePower = () => {
    console.log("Device power toggled on/off!");
    setIsDeviceOn((prev) => !prev);

  };

  // used for setting limits of settings
  const clamp = (val) => Math.max(1, Math.min(5, val));
  const tClamp = (val) => Math.max(1, Math.min(8, val));

  // Time Control Buttons (Hold logic)
  const handleHoldStart = (action) => {
    action();
    const interval = setInterval(action, 150);
    setHoldInterval(interval);
  };

  const handleHoldEnd = () => {
    clearInterval(holdInterval);
    setHoldInterval(null);
  };

  // Preset Long-Press
  const handlePresetHoldStart = () => {
    console.log("handlePresetHoldStart called");
    hasSavedPreset3.current = false;

    const timeout = setTimeout(() => {
      console.log("3 seconds reached => Saving preset 3");
      hasSavedPreset3.current = true;
      handleSavePreset3();
    }, 3000);

    setHoldCheckInterval(timeout);
  };

  const handlePresetHoldEnd = () => {
    console.log("handlePresetHoldEnd fired");
    clearTimeout(holdCheckInterval);
    setHoldCheckInterval(null);

    if (!hasSavedPreset3.current) {
      console.log("Short press => handlePreset()");
      handlePreset();
    }
  };

  // Settings Button Logic
  const handleSettingsMouseDown = () => {
    wasLongPress.current = false;
    const timeout = setTimeout(() => {
      console.log("Settings button long press");
      wasLongPress.current = true;
      if (currentSetting === 0) enterSettingsMode();
      else exitSettingsMode();
    }, 2000);

    setHoldSettingsTimeout(timeout);
  };

  const handleSettingsMouseUp = () => {
    clearTimeout(holdSettingsTimeout);
    setHoldSettingsTimeout(null);
    if (!wasLongPress.current) {
      console.log("Settings short press");
      if (currentSetting > 0) handleNextSetting();
    }
  };

  // === ADDED FOR DEVICE ON/OFF ===
  // Start/Pause Button: short press => normal start/pause, long press => device on/off
  const handleStartPauseDown = () => {
    wasStartPauseLongPress.current = false;
    const timeout = setTimeout(() => {
      wasStartPauseLongPress.current = true;
      toggleDevicePower(); // your device logic
    }, 2000);
    setStartPauseTimeout(timeout);
  };

  const handleStartPauseUp = () => {
    clearTimeout(startPauseTimeout);
    setStartPauseTimeout(null);
    if (!wasStartPauseLongPress.current) {
      // short press => normal start/pause
      handleStartPause();
    }
  };

    // Function to handle start of press (mouse or touch)
    const handleResetPressStart = () => {
      hasPerformedLongPress.current = false;
      // Start a timer for 2 seconds
      resetPressTimer.current = setTimeout(() => {
        hasPerformedLongPress.current = true;
        console.log("Long press detected: Reset to 00:00");
        handleLongReset();
      }, 2000); // 2 seconds for long press
    };
  
    // Function to handle end of press (mouse or touch)
    const handleResetPressEnd = () => {
      if (resetPressTimer.current) {
        clearTimeout(resetPressTimer.current);
        resetPressTimer.current = null;
        if (!hasPerformedLongPress.current) {
          console.log("Short press detected: Reset to initial time");
          handleReset();
        }
      }
    };
      
    // Cleanup in case the component unmounts while pressing
    useEffect(() => {
      return () => {
        if (resetPressTimer.current) {
          clearTimeout(resetPressTimer.current);
        }
      };
    }, []);

  return (
    <div className="controlsContainer">
      {/* Timer Adjustment Buttons */}
      {currentSetting === 0 && selectedTimer === "focus" && (
        <div className={`adjustButtons ${isDeviceOn ? 'visible' : 'hidden'}`}
        data-tooltip-id="adjustmentTip"
        >
          <button
            className="actionButton neuButtonS"
            onMouseDown={() => handleHoldStart(() => incrementFocusTime(60))}
            onMouseUp={handleHoldEnd}
            onMouseLeave={handleHoldEnd}
            disabled={isRunning || currentSetting > 0}
          >
            +1 Min
          </button>
          <button
            className="actionButton neuButtonS"
            onMouseDown={() => handleHoldStart(() => incrementFocusTime(5))}
            onMouseUp={handleHoldEnd}
            onMouseLeave={handleHoldEnd}
            disabled={isRunning || currentSetting > 0}
          >
            +5 Sec
          </button>
          <button
            className="actionButton neuButtonS"
            onMouseDown={() => handleHoldStart(() => decrementFocusTime(60))}
            onMouseUp={handleHoldEnd}
            onMouseLeave={handleHoldEnd}
            disabled={isRunning || currentSetting > 0}
          >
            -1 Min
          </button>
          <button
            className="actionButton neuButtonS"
            onMouseDown={() => handleHoldStart(() => decrementFocusTime(5))}
            onMouseUp={handleHoldEnd}
            onMouseLeave={handleHoldEnd}
            disabled={isRunning || currentSetting > 0}
          >
            -5 Sec
          </button>
        </div>
      )}

      {currentSetting === 0 && selectedTimer === "rest" && (
        <div className={`adjustButtons ${isDeviceOn ? 'visible' : 'hidden'}`}
        data-tooltip-id="adjustmentTip"
        >
          <button
            className="actionButton neuButtonS"
            onMouseDown={() => handleHoldStart(() => incrementRestTime(60))}
            onMouseUp={handleHoldEnd}
            onMouseLeave={handleHoldEnd}
            disabled={isRunning || currentSetting > 0}
          >
            +1 Min
          </button>
          <button
            className="actionButton neuButtonS"
            onMouseDown={() => handleHoldStart(() => incrementRestTime(5))}
            onMouseUp={handleHoldEnd}
            onMouseLeave={handleHoldEnd}
            disabled={isRunning || currentSetting > 0}
          >
            +5 Sec
          </button>
          <button
            className="actionButton neuButtonS"
            onMouseDown={() => handleHoldStart(() => decrementRestTime(60))}
            onMouseUp={handleHoldEnd}
            onMouseLeave={handleHoldEnd}
            disabled={isRunning || currentSetting > 0}
          >
            -1 Min
          </button>
          <button
            className="actionButton neuButtonS"
            onMouseDown={() => handleHoldStart(() => decrementRestTime(5))}
            onMouseUp={handleHoldEnd}
            onMouseLeave={handleHoldEnd}
            disabled={isRunning || currentSetting > 0}
          >
            -5 Sec
          </button>
        </div>
      )}
      <Tooltip id="adjustmentTip" className="toggleTips">
        <div>
          <h3>Adjust Time</h3>
          <p>For the purpose of this app we've used the buttons to simulate the time adjustment. <br></br><b>With the actual device:</b></p>
          <ul>
            <li><b>Slow turning</b> the knob will adjust the time by 5 seconds</li>
            <li><b>Fast turning</b> the knob will adjust the time by 1 minute</li>
          </ul>
        </div>
      </Tooltip>
      {/* If in settings mode => show relevant controls */}
      {currentSetting > 0 && (
        <div className="settingsAdjustButtons">
          {currentSetting === 1 && (
            <>
              <button className="actionButton neuButtonS" onClick={() => setBrightness(clamp(brightness - 1))}>
                - Brightness
              </button>
              <button className="actionButton neuButtonS" onClick={() => setBrightness(clamp(brightness + 1))}>
                + Brightness
              </button>
            </>
          )}
          {currentSetting === 2 && (
            <>
              <button className="actionButton neuButtonS" onClick={() => setVolume(clamp(volume - 1))}>- Volume</button>
              <button className="actionButton neuButtonS" onClick={() => setVolume(clamp(volume + 1))}>+ Volume</button>
            </>
          )}
          {currentSetting === 3 && (
            <>
              <button className="actionButton neuButtonS" onClick={() => setTone(tClamp(tone - 1))}>Prev Tone</button>
              <button className="actionButton neuButtonS" onClick={() => setTone(tClamp(tone + 1))}>Next Tone</button>
            </>
          )}
        </div>
      )}

      {/* Start/Pause Button */}
      <button
        className="startButton"
        onMouseDown={handleStartPauseDown}
        onMouseUp={handleStartPauseUp}
      >
        {isRunning ? "Pause" : "Start"}
      </button>

      {/* Reset Button */}
      <button 
        className="resetButton"
        style={{
          display: isDeviceOn ? "block" : "none"
        }} 
        onMouseDown={handleResetPressStart}
        onMouseUp={handleResetPressEnd} 
        disabled={isRunning}
        data-tooltip-id="resetToggleTip"
      >
        <p>Reset</p>
      </button>
      <Tooltip id="resetToggleTip" className="toggleTips">
        <div>
          <h3>Reset timer</h3>
          <p><b>Short press</b> to reset to last time that was started on timer.<br></br><b>Long press</b> to reset to 00:00</p>
        </div>
      </Tooltip>
      {/* Timer Toggle Button */}
      <button
        className="toggleButton neuButtonS"
        onClick={handleToggle}
        disabled={isRunning || currentSetting > 0}
        style={{
          display: isDeviceOn ? "block" : "none"
        }}
        data-tooltip-id="timerToggleTip"
      >
        <img src="/icons/T1T2.png" className="toggleIcon" alt="toggle icon" />
        Toggle Timer
      </button>
      <Tooltip id="timerToggleTip" className="toggleTips">
        <div>
          <h3>Toggle timer</h3>
          <p><b>Short press</b> to toggle between the focus and rest timer. The flashing timer will indicate which timer is active for your to set your desired time.</p>
        </div>
      </Tooltip>
      {/* Preset Toggle Button */}
      <button
        className="presetButton neuButtonS"
        onMouseDown={() => {
          console.log("Preset button onMouseDown fired");
          handlePresetHoldStart();
        }}
        onMouseUp={handlePresetHoldEnd}
        disabled={isRunning || currentSetting > 0}
        style={{
          display: isDeviceOn ? "block" : "none"
        }}
        data-tooltip-id="presetToggleTip"
      >
        <img src="/icons/Preset.png" className="toggleIcon" alt="toggle icon" />
        Toggle Presets
      </button>
      <Tooltip id="presetToggleTip" className="toggleTips">
        <div>
          <h3>Presets</h3>
          <p><b>Short press</b> to cycle between presets.<br></br><b>Long press</b> to store preset as preset 3.</p>
          <ul>
            <li><b>Preset 1:</b> 25 minutes / 5 minutes</li>
            <li><b>Preset 2:</b> 45 minutes / 15 minutes</li>
            <li><b>Preset 3:</b> User Defined</li>
          </ul>
        </div>
      </Tooltip>
      {/* Settings Button */}
      <button
        className="settingsButton neuButtonS"
        onMouseDown={handleSettingsMouseDown}
        onMouseUp={handleSettingsMouseUp}
        disabled={isRunning}
        style={{
          display: isDeviceOn ? "block" : "none"
        }}
        data-tooltip-id="settingsToggleTip"
      >
        <img src="/icons/Settings.png" className="toggleIcon" alt="toggle icon" />
        Settings
      </button>
      <Tooltip id="settingsToggleTip" className="toggleTips">
        <div>
          <h3>Settings</h3>
          <p><b>Long press</b> to enter & exit settings mode.<br></br><b>Short press</b> to cycle between the 3 settings.<br></br>Change Settings level by using the knob.</p>
          <ul>
            <li><b>Brightness:</b> 5 levels</li>
            <li><b>Volume:</b> 5 levels with Level 1 = Mute</li>
            <li><b>Tone:</b> 8 tones</li>
          </ul>
        </div>
      </Tooltip>
      {/* Pomodoro Mode Button */}
      <button
        className="pomodoroButton"
        onClick={handlePomodoroMode}
        style={{
          display: isDeviceOn ? "block" : "none"
        }}
        data-tooltip-id="pomodoroToggleTip"
      >
        <p>Pomodoro</p>
      </button>
      <Tooltip id="pomodoroToggleTip" className="toggleTips">
        <div>
          <h3>Pomodoro Mode</h3>
          <p><b>Short press</b> to toggle on/off Pomodoro Mode. <br></br>When Pomodoro Mode focus and break timers will automatically switch after 2 second notification.</p>
        </div>
      </Tooltip>
    </div>
  );
};

export default Controls;
